import { Injectable, inject } from '@angular/core';
import { NavController, Platform } from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import {
  Observable,
  catchError,
  firstValueFrom,
  of,
  timeout,
} from 'rxjs';
import {
  PermissionsEnum,
  RolesEnum,
} from 'src/app/core/constants/roles-and-permissions.enum';
import { AnalyticsService } from 'src/app/core/services/analytics.service';
import { ToolsService } from 'src/app/core/services/tools.service';
import { UserService } from 'src/app/core/services/user.service';
import {
  DocumentsToLearnComplianceService,
  PostAuthNavigationTarget,
} from 'src/app/shared/services/documents-to-learn/documents-to-learn-compliance.service';
import {
  AuthFlowComplete,
  AuthFlowId,
  AuthTransitionKey,
} from '../models/auth-flow.models';
import { LoginApiError, LoginApiResponse, LoginConfirmResponse, LoginMeta } from '../models/login-v2-api.models';
import {
  AuthFlowAnalyticsHelper,
  isLoginUserNotFoundMessage,
  shouldSuppressSendCodeErrorToast,
} from '../helpers/auth-analytics.helper';
import { AuthFlowService } from './auth-flow.service';
import { AuthSessionStore } from './auth-session.store';
import { LoginV2ApiService } from './login-v2-api.service';

@Injectable({ providedIn: 'root' })
export class AuthActionsService {
  private readonly loginApi = inject(LoginV2ApiService);
  private readonly store = inject(AuthSessionStore);
  private readonly flow = inject(AuthFlowService);
  private readonly navCtrl = inject(NavController);
  private readonly router = inject(Router);
  private readonly platform = inject(Platform);
  private readonly toolsService = inject(ToolsService);
  private readonly user = inject(UserService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly authAnalytics = inject(AuthFlowAnalyticsHelper);
  private readonly documentsCompliance = inject(DocumentsToLearnComplianceService);

  async loadPvzEmployees(): Promise<void> {
    const uuid = this.store.pvzUuid();
    if (!uuid) {
      return;
    }

    this.store.pvzEmployeesLoaded.set(false);
    await this.loginApi.presentLoading();
    this.loginApi.getPvzEmployees(uuid).subscribe({
      next: (res) => {
        this.loginApi.dismissLoading();
        if (res.success) {
          const items = res.items ?? [];
          this.store.setAccounts(items);
          this.authAnalytics.capturePvzEmployeesListShown(uuid, items.length);
          if (items.length === 0) {
            this.authAnalytics.capturePvzEmployeesListEmpty(uuid);
          }
          const pvzAddress = res.meta?.pvz_address ?? items[0]?.pvz_address;
          if (pvzAddress) {
            this.store.patchAccount({ pvz: pvzAddress });
          }
        }
        this.store.pvzEmployeesLoaded.set(true);
      },
      error: () => {
        this.loginApi.dismissLoading();
        this.store.pvzEmployeesLoaded.set(true);
      },
    });
  }

  /**
   * Вкладка «Регистрация» открыта (переключение или прямой заход на /registration).
   */
  trackRegistrationTabOpened(): void {
    this.authAnalytics.trackRegistrationTabOpened();
  }

  /**
   * Отправка кода на почту
   * @returns {Promise<{ success: boolean; message?: string }>} - результат отправки кода
   */
  async sendCode(): Promise<{ success: boolean; message?: string }> {
    const login = this.store.account().login.trim().toLowerCase();
    this.store.patchAccount({ login });

    const isRegistration = this.flow.flowId() === AuthFlowId.Registration;

    this.authAnalytics.captureSendCodeButtonClick(isRegistration);
    const request$ = isRegistration ? this.loginApi.sendRegisterCode(login) : this.loginApi.sendLoginCode(login);

    await this.loginApi.presentLoading(false);

    return new Promise((resolve) => {
      request$.subscribe({
        next: (res) => {
          this.loginApi.dismissLoading();
          if (this.isAuthError(res)) {
            if (isRegistration) {
              this.authAnalytics.captureRegistrationSendCodeError(res.message);
            } else {
              const errorProps = { Сообщение: res.message };
              if (isLoginUserNotFoundMessage(res.message)) {
                this.authAnalytics.captureLoginEvent(
                  'Вход: Ошибка — пользователь не найден',
                  errorProps,
                );
              } else {
                this.authAnalytics.captureLoginEvent(
                  'Вход: Ошибка при отправке кода на почту',
                  errorProps,
                );
              }
            }
            if (
              res.message &&
              res.code === undefined &&
              !shouldSuppressSendCodeErrorToast(res.message)
            ) {
              this.loginApi.toast(res.message, 'error', 'Ошибка');
            }
            resolve({ success: false, message: res.message });
            return;
          }

          if (isRegistration) {
            this.analyticsService.ymReachGoal('REGISTER_REGISTERED');
            this.authAnalytics.captureRegistrationScenarioEvent(
              'Вход: Собственник зарегистрирован',
            );
          }

          this.user.data.email = login;
          this.user.saveUserData();
          this.store.patchAccount({ attempts: null, code: '' });
          this.flow.goNext({ transition: AuthTransitionKey.Default });
          resolve({ success: true });
        },
        error: (error: unknown) => {
          console.log(error);
          this.loginApi.dismissLoading();
          this.loginApi.toast(
            'Не удалось отправить код. Повторите попытку позже.',
            'error',
            'Ошибка',
          );
          resolve({ success: false });
        },
      });
    });
  }

  /**
   * Подтверждение кода. На успехе с meta оверлей не снимаем — его держит вход в ЛК.
   * @param code - код для подтверждения
   * @returns {Promise<{ status: 'success' | 'invalid' | 'blocked'; message?: string }>} - статус подтверждения
   */
  async confirmCode(code: string): Promise<{
    status: 'success' | 'invalid' | 'blocked';
    message?: string;
  }> {
    const account = this.store.account();
    if (account.attempts === 0) {
      return { status: 'blocked' };
    }

    const trimmed = code.trim();
    if (!/\d{4}/.test(trimmed)) {
      this.loginApi.toast('Код должен состоять из 4 цифр.');
      return { status: 'invalid' };
    }

    const login = this.resolveLogin();
    if (!login) {
      this.loginApi.toast('Не указан email для входа.');
      return { status: 'invalid' };
    }

    this.store.patchAccount({ login, code: trimmed });

    await this.loginApi.presentLoading();

    return new Promise((resolve) => {
      const request$: Observable<LoginConfirmResponse> =
        this.flow.flowId() === AuthFlowId.Registration
          ? this.loginApi.confirmRegisterCode(login, trimmed)
          : this.loginApi.confirmLoginCode(login, trimmed);

      request$.subscribe({
        next: (res) => {
          if (!this.isAuthError(res)) {
            this.store.patchAccount({ attempts: null });
            if ('items' in res) {
              this.loginApi.dismissLoading();
              this.store.setAccounts(res.items);
              this.authAnalytics.captureLoginEvent('Вход: Шаг выбора аккаунта показан', {
                accounts_count: res.items.length,
              });
              this.flow.goNext({ transition: AuthTransitionKey.HasAccounts });
            } else if (this.flow.flowId() === AuthFlowId.Registration) {
              this.authAnalytics.captureRegistrationScenarioEvent('Вход: Регистрация — код принят');
              void this.continueAfterMeta(res.meta);
            } else {
              this.authAnalytics.rememberLoginSuccessMethod(
                'Вход по коду из почты',
              );
              void this.continueAfterMeta(res.meta);
            }
            resolve({ status: 'success' });
            return;
          }

          this.loginApi.dismissLoading();

          this.analyticsService.ymReachGoal('LOGIN_CODE_WRONG');

          if (this.flow.flowId() === AuthFlowId.Login) {
            this.authAnalytics.captureLoginEvent('Вход: Одноразовый код неверен');
          } else if (this.flow.flowId() === AuthFlowId.Registration) {
            this.authAnalytics.captureRegistrationScenarioEvent(
              'Вход: Регистрация — одноразовый код неверен',
            );
          }
          const attempts = this.extractLoginAttempts(res);
          this.store.patchAccount({ code: '', attempts });

          if (attempts === 0) {
            this.loginApi.toast(res.message ?? 'Превышено количество попыток. Запросите код повторно.', 'error', 'Ошибка');
            resolve({ status: 'blocked', message: res.message });
            return;
          }

          this.loginApi.toast(
            res.message,
            'error',
            'Ошибка',
          );
          resolve({ status: 'invalid', message: res.message });
        },
        error: (error: unknown) => {
          console.log(error);
          this.loginApi.dismissLoading();
          resolve({ status: 'invalid', message: error instanceof Error ? error.message : 'Ошибка при подтверждении кода' });
        },
      });
    });
  }

  async resendCode(): Promise<void> {
    const login = this.resolveLogin();
    if (!login) {
      this.loginApi.toast('Не указан email для входа.');
      return;
    }

    const isRegistration = this.flow.flowId() === AuthFlowId.Registration;

    if (isRegistration) {
      this.authAnalytics.captureRegistrationScenarioEvent(
        'Вход: Регистрация — повторная отправка кода на почту',
      );
    } else {
      this.authAnalytics.captureLoginEvent('Вход: Повторная отправка кода на почту');
    }
    await this.loginApi.presentLoading();
    this.store.patchAccount({ login, attempts: null, code: '' });
    const request$ =
      isRegistration ? this.loginApi.sendRegisterCode(login) : this.loginApi.sendLoginCode(login);

    request$.subscribe({
      next: (res) => {
        this.loginApi.dismissLoading();
        if (!this.isAuthError(res)) {
          this.loginApi.toast('Код для входа в личный кабинет выслан повторно.', 'info');
          return;
        }

        if (isRegistration) {
          this.authAnalytics.captureRegistrationSendCodeError(res.message);
        } else if (res.message && res.code === undefined) {
          this.loginApi.toast(res.message, 'error', 'Ошибка');
        }
      },
    });
  }

  /**
   * Выбор аккаунта. Оверлей не снимаем на успехе — его держит переход в ЛК.
   */
  async selectAccount(id: number): Promise<void> {
    const login = this.resolveLogin();
    if (!login) {
      this.loginApi.toast('Не указан email для входа.');
      return;
    }

    const code = this.store.account().code?.trim() ?? '';
    if (!code) {
      this.loginApi.toast('Не указан код для входа.');
      return;
    }

    const account = this.store.accounts().find((item) => item.id === id);
    this.authAnalytics.captureLoginEvent('Вход: Учётная запись выбрана', {
      account_type:
        account && account.name === account.owner_name ? 'owner' : 'employee',
    });

    await this.loginApi.presentLoading();
    this.loginApi.selectAccount(login, id, code).subscribe({
      next: (res) => {
        if (!this.isAuthError(res)) {
          this.authAnalytics.rememberLoginSuccessMethod('Выбор аккаунта');
          void this.continueAfterMeta(res.meta);
          return;
        }

        this.loginApi.dismissLoading();
        if (res.message && res.code === undefined) {
          this.loginApi.toast(res.message, 'error', 'Ошибка');
        }
      },
      error: () => {
        this.loginApi.dismissLoading();
      },
    });
  }

  selectPvzAccount(id: number): void {
    if (id > 0) {
      this.authAnalytics.capturePvzAccountSelected();
      this.store.patchAccount({ id, login: '' });
      this.flow.goNext({ transition: AuthTransitionKey.PvzPincode });
    } else {
      this.authAnalytics.capturePvzSubstituteLoginClicked();
      this.store.patchAccount({ id: null });
      this.flow.goNext({ transition: AuthTransitionKey.PvzSubstitute });
    }
  }

  /**
   * Пинкод ПВЗ. На успехе оверлей не снимаем — его держит doLogin.
   */
  async confirmPincode(code: string): Promise<{
    success: boolean;
    message?: string;
    login_attempts?: number;
  }> {
    const trimmed = code.trim();
    if (!/\d{4}/.test(trimmed)) {
      this.loginApi.toast('Код должен состоять из 4 цифр.');
      return { success: false };
    }

    this.store.patchAccount({ code: trimmed });

    const uuid = this.store.pvzUuid();
    if (!uuid) {
      return { success: false };
    }

    await this.loginApi.presentLoading();

    return new Promise((resolve) => {
      this.loginApi
        .confirmPvzPincode({
          code: trimmed,
          id: this.store.account().id,
          login: this.store.account().login,
          pvz_uuid: uuid,
        })
        .subscribe({
          next: (res) => {
            if (!this.isAuthError(res)) {
              this.store.patchAccount({ attempts: null });
              const accountId = this.store.account().id;
              if (accountId != null && accountId > 0) {
                this.authAnalytics.capturePvzSuccessByPincode();
              } else {
                this.authAnalytics.capturePvzSuccessBySubstitute();
              }
              void this.doLogin(res.meta, { continueFlow: true });
              resolve({ success: true });
              return;
            }

            this.loginApi.dismissLoading();

            this.analyticsService.ymReachGoal('LOGIN_CODE_WRONG');
            this.authAnalytics.capturePvzPincodeWrong();
            const rawAttempts = res.login_attempts;
            const attempts = Number(rawAttempts);
            const hasAttempts =
              rawAttempts !== null &&
              rawAttempts !== undefined &&
              Number.isFinite(attempts);
            this.store.patchAccount({
              code: '',
              ...(hasAttempts ? { attempts } : {}),
            });
            resolve({
              success: false,
              message: res.message,
              login_attempts: hasAttempts ? attempts : undefined,
            });
          },
          error: () => {
            this.loginApi.dismissLoading();
            resolve({ success: false });
          },
        });
    });
  }

  async confirmSubstitute(
    login: string,
    code: string,
  ): Promise<{
    success: boolean;
    message?: string;
    login_attempts?: number;
  } | null> {
    const trimmedCode = code.trim();
    if (!/\d{4}/.test(trimmedCode)) {
      this.loginApi.toast('Пинкод должен состоять из 4 цифр.');
      return null;
    }

    let normalizedLogin = login.trim();
    if (this.toolsService.emailValid(normalizedLogin)) {
      normalizedLogin = normalizedLogin.toLowerCase();
    } else if (this.toolsService.phoneValid(normalizedLogin)) {
      normalizedLogin = normalizedLogin.replace(/[^\d]/g, '');
    } else {
      return { success: false, message: 'Для входа в личный кабинет укажите ваш адрес электронной почты или номер телефона.' };
    }

    this.store.patchAccount({
      login: normalizedLogin,
      code: trimmedCode,
    });
    return this.confirmPincode(trimmedCode);
  }

  async showConfirmWidget(
    tel: string,
  ): Promise<{ success: boolean; message?: string }> {
    if (!this.toolsService.phoneValid(tel)) {
      return { success: false };
    }

    this.store.patchAccount({ tel });

    const token = this.store.accountData()?.token;
    if (!token) {
      return { success: false };
    }

    await this.loginApi.presentLoading();

    return new Promise((resolve) => {
      this.loginApi.checkTel(token, tel).subscribe({
        next: (res) => {
          this.loginApi.dismissLoading();
          if (res.success) {
            this.store.widgetFailedToLoad.set(false);
            if (this.flow.flowId() === AuthFlowId.Login) {
              this.authAnalytics.captureLoginTelWidgetStepShown();
            } else if (this.flow.flowId() === AuthFlowId.Registration) {
              this.authAnalytics.captureRegistrationTelWidgetStepShown();
            }
            resolve({ success: true });
            return;
          }

          if (this.flow.flowId() === AuthFlowId.Login) {
            this.authAnalytics.captureLoginTelNumberUnavailable(res.message);
          } else if (this.flow.flowId() === AuthFlowId.Registration) {
            this.authAnalytics.captureRegistrationTelNumberUnavailable(
              res.message,
            );
          }
          resolve({ success: false, message: res.message });
        },
        error: () => {
          this.loginApi.dismissLoading();
          resolve({ success: false });
        },
      });
    });
  }

  confirmWidgetFailedToLoad(): void {
    if (this.flow.flowId() === AuthFlowId.Login) {
      this.authAnalytics.captureLoginTelWidgetFailedToLoad();
      this.store.widgetFailedToLoad.set(true);
    } else if (this.flow.flowId() === AuthFlowId.Registration) {
      this.authAnalytics.captureRegistrationTelWidgetFailedToLoad();
    }
    this.loginApi.toast(
      'Не удалось загрузить подтверждение телефона. Попробуйте ещё раз.',
      'error',
      'Ошибка',
    );
  }

  async skipTelConfirmation(): Promise<void> {
    const accountData = this.store.accountData();
    if (!accountData) {
      return;
    }

    const isRegistration = this.flow.flowId() === AuthFlowId.Registration;

    if (isRegistration) {
      this.store.patchRegistrationPath({ phoneSkipped: true });
      this.authAnalytics.captureRegistrationTelSkipClicked();
    } else {
      this.authAnalytics.captureLoginTelSkipClicked();
      this.authAnalytics.captureLoginSuccessAfterTelSkip();
    }

    void this.doLogin(accountData, { continueFlow: true });
  }

  completeTelVerification(): void {
    const accountData = this.store.accountData();
    if (!accountData) {
      return;
    }

    if (this.flow.flowId() === AuthFlowId.Login) {
      this.authAnalytics.captureLoginTelConfirmed();
      this.authAnalytics.captureLoginSuccessAfterTelConfirm();
    } else if (this.flow.flowId() === AuthFlowId.Registration) {
      this.authAnalytics.captureRegistrationTelConfirmed();
    }

    void this.doLogin(accountData, { continueFlow: true });
  }

  async skipOnboarding(): Promise<void> {
    this.store.patchRegistrationPath({ onboardingSkipped: true });
    await this.finishOnboarding();
  }

  /**
   * Выполнение входа. Complete — оверлей до ЛК; иначе снимаем перед следующим шагом.
   * @param meta - мета данные пользователя
   * @param options.continueFlow - продолжить сценарий из AUTH_FLOWS
   * @param options.transition - переход из текущего шага
   * @returns {Promise<void>} - Promise, который разрешается после выполнения входа
   */
  async doLogin(
    meta: LoginMeta,
    options?: {
      continueFlow?: boolean;
      transition?: AuthTransitionKey;
    },
  ): Promise<void> {
    await this.loginApi.presentLoading();
    this.analyticsService.ymReachGoal('LOGIN');
    this.user.data.id = meta.id;
    this.user.data.email = meta.email;
    this.user.data.tawkHash = meta.tawk_hash;
    this.user.data.name = meta.name;
    this.user.data.role = meta.role;
    this.user.data.roleName = meta.role_name;
    this.user.data.attachedPvzs = meta.attached_pvzs;
    this.user.data.subType = meta.sub_type;
    this.user.data.subActive = meta.sub_active;
    this.user.data.showNPSButton = meta.show_nps;
    this.user.data.showPartnersPage = meta.show_partners;
    this.user.data.showChats = Boolean(meta.show_chats);

    const pvzUuid = this.store.pvzUuid();
    if (pvzUuid) {
      this.user.data.pvzUUID = pvzUuid;
    }

    this.user.setPermissions(meta.permissions);

    if (
      this.user.data.role === RolesEnum.ROLE_OWNER ||
      this.user.checkPermissions([PermissionsEnum.PERM_SUB_MANAGE])
    ) {
      this.user.data.subUntil = meta.sub_until;
      this.user.data.subPrice = meta.sub_price;
      this.user.data.subLicenses = meta.sub_licenses;
      this.user.subPlans = meta.sub_plans;
    }

    if (meta.settings) {
      this.user.applySettings(meta.settings);
    }

    this.user.saveUserData();
    this.loginApi.setSessionToken(meta.token);
    this.analyticsService.ymUserParams();
    this.analyticsService.phIdentify();

    if (options?.continueFlow) {
      this.store.pendingPostAuth.set({ needUpdate: meta.update });
      const nextStep = this.flow.goNext({
        transition: options.transition ?? AuthTransitionKey.Default,
      });
      if (!nextStep || nextStep === AuthFlowComplete.Complete) {
        this.store.pendingPostAuth.set(null);
        await this.navigateAfterAuth(meta.update);
        return;
      }

      this.loginApi.dismissLoading();
      return;
    }

    await this.navigateAfterAuth(meta.update);
  }

  /** Финиш онбординга: оверлей поднимает navigateAfterAuth. */
  async finishOnboarding(): Promise<void> {
    const pending = this.store.pendingPostAuth();
    this.store.pendingPostAuth.set(null);
    await this.navigateAfterAuth(pending?.needUpdate ?? false);
  }

  /**
   * Переход в ЛК или introduction. Оверлей до await навигации, снятие в finally.
   * @param needUpdate - нужно обновить данные пользователя после входа
   */
  private async navigateAfterAuth(needUpdate: boolean): Promise<void> {
    await this.loginApi.presentLoading();

    if (this.store.isNewUser()) {
      this.authAnalytics.captureRegistrationSuccess();
    } else {
      this.authAnalytics.capturePendingLoginSuccess();
    }

    const fallback = this.fallbackAppDestination(needUpdate);
    try {
      const destination = await firstValueFrom(
        this.documentsCompliance
          .resolveDestinationAfterAuth({
            isNewUser: this.store.isNewUser(),
            needUpdate,
          })
          .pipe(
            timeout({ first: 10_000 }),
            catchError(() => of(fallback)),
          ),
      );
      await this.navigateToPostAuth(destination);
    } finally {
      this.loginApi.dismissLoading();
    }
  }

  /** Запасной маршрут в ЛК, если проверка документов не успела. */
  private fallbackAppDestination(
    needUpdate: boolean,
  ): PostAuthNavigationTarget {
    return {
      commands: ['/app'],
      state: {
        isNewUser: this.store.isNewUser(),
        needUpdate,
        noCheck: true,
      },
    };
  }

  /** Корневая навигация на назначение после авторизации. */
  private async navigateToPostAuth(
    destination: PostAuthNavigationTarget,
  ): Promise<void> {
    this.navCtrl.setDirection('root');
    const redirectUrl = this.store.redirectUrl();
    if (redirectUrl && destination.commands[0] === '/app') {
      await this.router.navigateByUrl(redirectUrl, {
        replaceUrl: true,
        state: destination.state,
      });
      return;
    }

    await this.router.navigate(destination.commands, {
      replaceUrl: true,
      state: destination.state,
      queryParams: redirectUrl ? { redirect: redirectUrl } : undefined,
    });
  }

  checkPvzDesktopOnly(): boolean {
    const uuid = this.store.pvzUuid();
    if (uuid && !this.platform.is('desktop')) {
      this.authAnalytics.capturePvzMobileBlocked();
      this.loginApi.toast('Вход по ссылке ПВЗ доступен только с компьютера на пункте выдачи.');
      this.router.navigate(['/login'], { replaceUrl: true });
      return false;
    }
    return true;
  }

  private resolveLogin(): string {
    const fromStore = this.store.account().login?.trim() ?? '';
    const fromUser = this.user.data.email?.trim() ?? '';
    return (fromStore || fromUser).toLowerCase();
  }

  /**
   * После meta: token в session, затем tel / onboarding / ЛК по графу флоу.
   */
  private continueAfterMeta(meta: LoginMeta): Promise<void> {
    const needTel =
      Boolean(meta.tel_verify?.need_to_verify) &&
      !this.store.widgetFailedToLoad();
    const transition = needTel
      ? AuthTransitionKey.NeedTel
      : AuthTransitionKey.Default;

    if (needTel && this.flow.flowId() === AuthFlowId.Login) {
      this.authAnalytics.captureLoginTelInputStepShown();
    } else if (needTel && this.flow.flowId() === AuthFlowId.Registration) {
      this.authAnalytics.captureRegistrationTelInputStepShown();
    }

    this.store.setAccountData(meta);
    return this.doLogin(meta, { continueFlow: true, transition });
  }

  private isAuthError(res: LoginApiResponse): res is LoginApiError {
    return res.success === false;
  }

  private extractLoginAttempts(res: LoginApiError): number {
    const raw = res.login_attempts;
    if (raw === null || raw === undefined) {
      return 0;
    }
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
}
